import { useMemo } from "react";

import type { AxiosRequestConfig } from "axios";
import config from "config/appConfig";

import { DateTime, Interval } from "luxon";

import useLocalStorage from "shared/services/localStorage/localStorage";
import type { AdminRoles } from "shared/types/Admin";
import type { UserRole } from "shared/types/UserModel";
import { safelyParseJSON } from "utils/serviceUtils/helpers";

/**
 * Encapsulated JWT cache to avoid bare module-level mutable state.
 * This is a singleton cache for in-memory token storage.
 */
class JwtCache {
	private token = "";
	private expiredTime = 0;

	getToken(): string {
		return this.token;
	}

	getExpiredTime(): number {
		return this.expiredTime;
	}

	set(token: string, expiredTime: number): void {
		this.token = token;
		this.expiredTime = expiredTime;
	}

	clear(): void {
		this.token = "";
		this.expiredTime = 0;
	}
}

const jwtCache = new JwtCache();

const useJwt = () => {
	const localStorage = useLocalStorage();

	return useMemo(() => {
		const refreshToken = async () => {};

		const setJwt = (token: string, expired: number): void => {
			jwtCache.set(token, expired);

			localStorage.set(config.TOKEN_KEY, token);
			localStorage.set(config.TOKEN_EXPIRED_KEY, expired.toString());
		};

		const getJwt = async (): Promise<string> => {
			try {
				const token = jwtCache.getToken() || (localStorage.get(config.TOKEN_KEY, null) as string);
				const expiredTime = jwtCache.getExpiredTime() || (localStorage.get(config.TOKEN_EXPIRED_KEY, 0) as number);

				const validToken = token;
				if (expiredTime) {
					const expiredDatetime = DateTime.fromSeconds(expiredTime);
					const currentDatetime = DateTime.now();
					const diff = Interval.fromDateTimes(currentDatetime, expiredDatetime);
					const diffMinutes = diff.length("minutes");
					if (isNaN(diffMinutes) || diffMinutes < 15) {
						await refreshToken();
					}
				}

				return validToken;
			} catch (err) {
				console.warn(err);
				// navigate(authRoutes.signIn.getPath());
				return "";
			} finally {
				// releaseLock();
			}
		};

		const hasJwt = (): boolean => {
			const token = jwtCache.getToken() || (localStorage.get(config.TOKEN_KEY, null) as string);
			return !!token;
		};

		return {
			hasJwt,
			getJwt,
			setJwt,
			injectJwt: async (config: AxiosRequestConfig | undefined) => {
				const token = await getJwt();
				const header = token ? { Authorization: `Bearer ${token}` } : {};
				return {
					...config,
					headers: {
						"Content-Type": "application/json",
						...header,
						...(config ? config.headers || {} : {})
					}
				};
			},
			parseJwt: () => {
				const emptyData = {
					email: "",
					role: ""
				};
				const token = jwtCache.getToken() || (localStorage.get(config.TOKEN_KEY, null) as string);
				if (token) {
					const tokenPublicData = token.split(".")[1];
					const data = safelyParseJSON<{ email: string; role: UserRole | AdminRoles }>(atob(tokenPublicData!));
					return data || emptyData;
				}
				return emptyData;
			},
			injectBase: (config: AxiosRequestConfig | undefined) => {
				return {
					...config,
					headers: {
						"Content-Type": "application/json",
						...(config ? config.headers || {} : {})
					}
				};
			},
			clearJwt: () => {
				jwtCache.clear();
				localStorage.remove(config.TOKEN_KEY);
			}
		};
	}, [localStorage]);
};

export default useJwt;
